// Toolbar extensions button + panel, and the incognito indicator. The panel lists the toolbar
// buttons that didn't fit ("More from your toolbar", app/ext-bar.js) and then every extension
// grouped by category, each with a switch, a "Show in toolbar" pin and quick actions, plus a link
// into Settings > Extensions.
import { get, set, subscribe } from "../shared/settings-store.js";
// The panel is fully rebuilt (buildBody) on any settings change while it's open,
// so individual switches don't subscribe - that would leak a subscriber per
// rebuild. They just read state at build time and write on click.
import { EXTENSIONS, EXT_CATEGORIES, EXTENSION_MAP, extAvailable } from "../shared/extensions.js";
import { canPin, isPinned, pinPatch } from "../shared/ext-pins.js";
import { setMany } from "../shared/settings-store.js";
import { openSettings } from "./tabs.js";
import { openDevtoolsActive, clearHighlightsActive, pipActive } from "./ext-frames.js";
import { readGate } from "./read-gate.js";
import { overflowExts } from "./ext-bar.js";

const $ = (id) => document.getElementById(id);

// Quick actions surfaced under a page-tool extension's row when it's enabled.
const ACTIONS = {
	devtools: [{ label: "Open on this page", run: () => openDevtoolsActive() }],
	readaloud: [{ label: () => (readGate.reading() ? "Show the player" : "Read this page"), run: () => readGate.toggle(btn) }],
	highlighter: [{ label: "Clear highlights", run: () => clearHighlightsActive() }],
	pip: [{ label: "Pop out video", run: () => pipActive() }],
};

let panel = null;
let btn = null;

function makeSwitch(key, nameId, descId) {
	const b = document.createElement("button");
	b.className = "rv-switch rv-switch-sm";
	b.type = "button";
	b.setAttribute("role", "switch");
	b.setAttribute("aria-labelledby", nameId);
	b.setAttribute("aria-describedby", descId);
	b.dataset.key = key;
	const v = !!get(key);
	b.dataset.on = v ? "1" : "0";
	b.setAttribute("aria-checked", String(v));
	b.addEventListener("click", (e) => {
		e.stopPropagation();
		set(key, !get(key));
		buildBody();
		// The body was rebuilt; keep focus on the same switch.
		const again = panel && panel.querySelector('.rv-switch[data-key="' + key + '"]');
		if (again) again.focus();
	});
	return b;
}

// "Show in toolbar" for one extension.
function makePin(ext) {
	const b = document.createElement("button");
	b.type = "button";
	b.className = "rv-extp-pin";
	const on = isPinned(ext.id, get);
	b.setAttribute("aria-pressed", String(on));
	b.setAttribute("aria-label", "Show " + ext.label + " in the toolbar");
	b.title = on ? "In the toolbar" : "Not in the toolbar";
	b.dataset.pin = ext.id;
	b.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" aria-hidden="true"><path d="M9.5 1.5L6 5 3 6l3 3-3.5 3.5 1 1L10 10l3 3 1-1-1-3 3.5-3.5z"/></svg>';
	b.addEventListener("click", (e) => {
		e.stopPropagation();
		setMany(pinPatch(ext.id, !isPinned(ext.id, get), get));
		buildBody();
		const again = panel && panel.querySelector('[data-pin="' + ext.id + '"]');
		if (again) again.focus();
	});
	return b;
}

function buildBody() {
	if (!panel) return;
	const body = panel.querySelector(".rv-extp-body");
	body.textContent = "";
	// Buttons that didn't fit beside the address bar, with their actions.
	const more = overflowExts();
	if (more.length) {
		const head = document.createElement("div");
		head.className = "rv-extp-cat";
		head.setAttribute("role", "heading");
		head.setAttribute("aria-level", "3");
		head.textContent = "More from your toolbar";
		body.appendChild(head);
		for (const id of more) {
			const ext = EXTENSION_MAP[id];
			const rowEl = document.createElement("div");
			rowEl.className = "rv-extp-row on";
			const ic = document.createElement("span");
			ic.className = "rv-extp-ic";
			ic.setAttribute("aria-hidden", "true");
			ic.innerHTML = ext.icon;
			const txt = document.createElement("div");
			txt.className = "rv-extp-txt";
			const name = document.createElement("div");
			name.className = "rv-extp-name";
			name.textContent = ext.label;
			txt.append(name);
			const actWrap = document.createElement("div");
			actWrap.className = "rv-extp-acts";
			for (const a of ACTIONS[id] || []) {
				const ab = document.createElement("button");
				ab.type = "button";
				ab.className = "rv-extp-act";
				ab.textContent = typeof a.label === "function" ? a.label() : a.label;
				ab.addEventListener("click", (e) => { e.stopPropagation(); closePanel(); a.run(); });
				actWrap.appendChild(ab);
			}
			if (actWrap.children.length) txt.append(actWrap);
			rowEl.append(ic, txt);
			body.appendChild(rowEl);
		}
		const all = document.createElement("div");
		all.className = "rv-extp-cat";
		all.setAttribute("role", "heading");
		all.setAttribute("aria-level", "3");
		all.textContent = "All extensions";
		body.appendChild(all);
	}
	EXT_CATEGORIES.forEach((cat) => {
		const inCat = EXTENSIONS.filter((x) => x.category === cat && extAvailable(x));
		if (!inCat.length) return;
		const head = document.createElement("div");
		head.className = "rv-extp-cat";
		head.setAttribute("role", "heading");
		head.setAttribute("aria-level", "3");
		head.textContent = cat;
		body.appendChild(head);
		inCat.forEach((ext) => {
			const on = get(ext.key) === true;
			const rowEl = document.createElement("div");
			rowEl.className = "rv-extp-row" + (on ? " on" : "");
			const ic = document.createElement("span");
			ic.className = "rv-extp-ic";
			ic.innerHTML = ext.icon;
			const txt = document.createElement("div");
			txt.className = "rv-extp-txt";
			const name = document.createElement("div");
			name.className = "rv-extp-name";
			name.id = "rv-extp-name-" + ext.id;
			name.textContent = ext.label;
			const desc = document.createElement("div");
			desc.className = "rv-extp-desc";
			desc.id = "rv-extp-desc-" + ext.id;
			desc.textContent = ext.desc;
			txt.append(name, desc);
			// Quick actions for an enabled page tool.
			const acts = on ? ACTIONS[ext.id] : null;
			if (acts) {
				const actWrap = document.createElement("div");
				actWrap.className = "rv-extp-acts";
				acts.forEach((a) => {
					const ab = document.createElement("button");
					ab.type = "button";
					ab.className = "rv-extp-act";
					ab.textContent = typeof a.label === "function" ? a.label() : a.label;
					ab.addEventListener("click", (e) => { e.stopPropagation(); closePanel(); a.run(); });
					actWrap.appendChild(ab);
				});
				txt.appendChild(actWrap);
			}
			ic.setAttribute("aria-hidden", "true");
			const ctl = document.createElement("div");
			ctl.className = "rv-extp-ctl";
			if (canPin(ext.id)) ctl.append(makePin(ext));
			ctl.append(makeSwitch(ext.key, name.id, desc.id));
			rowEl.append(ic, txt, ctl);
			body.appendChild(rowEl);
		});
	});
}

function ensurePanel() {
	if (panel) return panel;
	panel = document.createElement("div");
	panel.className = "rv-extp";
	panel.hidden = true;
	panel.id = "rv-ext-panel";
	panel.setAttribute("role", "dialog");
	panel.setAttribute("aria-labelledby", "rv-extp-title");
	const head = document.createElement("div");
	head.className = "rv-extp-head";
	head.innerHTML = '<strong id="rv-extp-title">Extensions</strong><span class="rv-extp-sub">Turned-on tools get a button in the toolbar</span>';
	const body = document.createElement("div");
	body.className = "rv-extp-body";
	const foot = document.createElement("div");
	foot.className = "rv-extp-foot";
	const manage = document.createElement("button");
	manage.type = "button";
	manage.className = "rv-extp-manage";
	manage.textContent = "Manage in Settings";
	manage.addEventListener("click", () => { closePanel(); openSettings(); });
	foot.appendChild(manage);
	panel.append(head, body, foot);
	document.body.appendChild(panel);
	if (btn) btn.setAttribute("aria-controls", panel.id);
	document.addEventListener("pointerdown", (e) => { if (panel && !panel.hidden && !panel.contains(e.target) && e.target !== btn && !(btn && btn.contains(e.target))) closePanel(); }, true);
	document.addEventListener("keydown", (e) => { if (e.key === "Escape" && panel && !panel.hidden) closePanel(true); });
	// Tab and Shift+Tab cycle inside the open panel; Escape closes it.
	panel.addEventListener("keydown", (e) => {
		if (e.key !== "Tab") return;
		const items = [...panel.querySelectorAll("button:not([disabled])")];
		if (!items.length) return;
		const first = items[0], last = items[items.length - 1];
		if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
		else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
	});
	window.addEventListener("resize", () => closePanel());
	return panel;
}

function position() {
	if (!panel || !btn) return;
	const r = btn.getBoundingClientRect();
	const w = panel.offsetWidth || 320;
	panel.style.top = r.bottom + 6 + "px";
	panel.style.left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8)) + "px";
}

function openPanel() {
	ensurePanel();
	buildBody();
	panel.hidden = false;
	position();
	if (btn) btn.setAttribute("aria-expanded", "true");
	const first = panel.querySelector(".rv-switch");
	if (first) first.focus();
}
function closePanel(restore) {
	if (!panel || panel.hidden) return;
	const inside = panel.contains(document.activeElement);
	panel.hidden = true;
	if (btn) btn.setAttribute("aria-expanded", "false");
	if ((restore || inside) && btn) { try { btn.focus(); } catch (e) {} }
}
function togglePanel() { if (panel && !panel.hidden) closePanel(); else openPanel(); }

// Reflect the number of enabled extensions as a small badge on the button.
function updateCount() {
	const countEl = $("rv-ext-count");
	if (!countEl) return;
	const n = EXTENSIONS.filter((x) => !x.quiet && get(x.key) === true).length;
	if (n > 0) { countEl.textContent = String(n); countEl.hidden = false; }
	else countEl.hidden = true;
}

// Show/hide the incognito toolbar indicator (and let it toggle incognito off).
function updateIncognito() {
	const el = $("rv-incognito");
	if (!el) return;
	el.hidden = get("incognito") !== true;
}

export function initExtUI() {
	btn = $("rv-ext");
	if (btn) {
		btn.setAttribute("aria-haspopup", "dialog");
		btn.addEventListener("click", togglePanel);
	}

	const inc = $("rv-incognito");
	if (inc) inc.addEventListener("click", () => set("incognito", false));

	updateCount();
	updateIncognito();
	subscribe(() => { updateCount(); updateIncognito(); if (panel && !panel.hidden) buildBody(); });
}
